import React, { memo, useCallback, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { EntryDetails } from '@/api/entries/types/types';
import { EventInfo } from '@/api/events/types/types';
import { LineSeparator } from '@/components/LineSeparator';
import { EntryCashOutControls, EntryCashOutControlsProps } from '@/components/entry-card';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { CancelEntryButton } from '@/feature/cancel-entry/components/CancelEntryButton';
import { CashableEntry, useCashOutPolling } from '@/feature/entries-pickem/hooks/use-cash-out';
import { useEntryAvailableActions } from '@/feature/entries-pickem/hooks/use-entry-available-actions';
import { usePickemCashedOutEntries } from '@/feature/entries-pickem/hooks/use-pickem-cashed-out-entries';
import { usePickemEntryCashOut } from '@/feature/entries-pickem/hooks/use-pickem-entry-cash-out';
import { ViewP2pLeaderboardsButton } from '@/feature/p2p-leaderboards/components/ViewP2pLeaderboardsButton';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { GameType } from '@/types/api.generated';

const CANCEL_EXPIRY_REFETCH_DELAY = 1500;

const CashOutTray = memo((controls: EntryCashOutControlsProps) => {
    const safeInsets = useSafeAreaInsets();
    return (
        <Box style={{ paddingBottom: safeInsets.bottom }} backgroundColor={ds.colorToken.background.neutral.base}>
            <LineSeparator />
            <Box padding="s16">
                <EntryCashOutControls {...controls} />
            </Box>
        </Box>
    );
});

type EntryActionsProps = {
    entry: EntryDetails;
    events: EventInfo[];
    onCancelExpired?: () => void;
};

export const EntryActions = memo(({ entry, events, onCancelExpired }: EntryActionsProps) => {
    const safeInsets = useSafeAreaInsets();
    const [hideCancelButton, setHideCancelButton] = useState(false);
    const { canCashout, canCancel } = useEntryAvailableActions(entry);
    const cashableEntry: CashableEntry = { id: entry?.id ?? '', status: entry?.status, canBeCashedout: canCashout };
    const cashedOut = usePickemCashedOutEntries(s => s.entries.get(cashableEntry.id)) ?? null;
    const cashoutOffer = useCashOutPolling(cashableEntry, canCashout && !cashedOut);
    const { controls: cashOutControls } = usePickemEntryCashOut(cashableEntry, cashoutOffer ?? null, 'details');
    const showCancelButton = canCancel && !hideCancelButton;
    const showCashOutTray = canCashout && !!cashOutControls;
    const showLeaderboards = entry?.gameType === GameType.P2p && !!entry?.id;
    const leaderboardsBottomInset = showCancelButton || showCashOutTray ? 0 : safeInsets.bottom;

    const handleCancelExpired = useCallback(() => {
        // refetch with delay to allow BE time to switch the entry from cancelable to not cancelable
        setTimeout(() => onCancelExpired?.(), CANCEL_EXPIRY_REFETCH_DELAY);
    }, [onCancelExpired]);

    return (
        <MaxWidthWrapper position="absolute" width={'100%'} bottom={0} pointerEvents="box-none">
            {showLeaderboards ? (
                <Box style={{ paddingBottom: leaderboardsBottomInset }} pointerEvents="box-none">
                    <ViewP2pLeaderboardsButton id={entry?.id} />
                </Box>
            ) : null}
            {showCancelButton ? (
                <Box
                    style={{ paddingBottom: safeInsets.bottom }}
                    pointerEvents="box-none"
                    backgroundColor={ds.colorToken.background.neutral.base}
                >
                    <LineSeparator />
                    <Box padding="s16" pointerEvents="box-none">
                        <CancelEntryButton
                            entry={entry}
                            setHideCancelButton={setHideCancelButton}
                            events={events}
                            onExpired={handleCancelExpired}
                        />
                    </Box>
                </Box>
            ) : null}
            {showCashOutTray ? <CashOutTray {...cashOutControls} /> : null}
        </MaxWidthWrapper>
    );
});
